"use client"

/**
 * SimpleSelect: the ONE simple dropdown (docs/THEME.md 2.10, 2.16; std-overlays foundation). A single choice of plain
 * labels, optionally with an icon or a short description, on the shared Select (Radix) in look="standard". Long lists
 * that need typing to find use Combobox; several choices use MultiSelect (components/ui/combobox.tsx); a native
 * `<select>` converts through NativeSelectAdapter (components/ui/native-select-adapter.tsx), which is built on this.
 *
 *   <SimpleSelect aria-label="Language" value={lang} onValueChange={setLang}
 *     options={[{ value: 'en', label: 'English' }, { value: 'fr', label: 'French', description: 'Canada' }]} />
 *
 * Empty values: an option whose value is "" is rendered with the reserved item value EMPTY_SELECT_VALUE and
 * translated back to "" here, so `SelectItem value=""` (which Radix rejects) is never rendered and callers still
 * see "". Form fields (`name`, `required`, `form`): this component renders ONE visually hidden native `<select>`
 * holding the real values (including ""), so the form submits exactly what a native select would, `required` and
 * form reset behave natively, and an invalid submit moves focus to the trigger. Radix's own hidden select is never
 * given the name (it would submit the reserved value, or nothing for "").
 */
import * as React from "react"

import { cn } from "@/lib/utils"

import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "./select"
import { overlay, type OptionTone, type OverlaySize } from "./overlay-recipes"

export interface SelectOption {
  value: string
  label: React.ReactNode
  /** Plain text for typeahead and search when the label is not a string. */
  textValue?: string
  icon?: React.ReactNode
  /** The icon's colour (a token tone); with iconTile the icon sits on a tinted square. */
  iconTone?: OptionTone
  iconTile?: boolean
  /** A colour swatch before the label (list and trigger). */
  swatch?: OptionTone
  description?: React.ReactNode
  /** A trailing count or tag (list only). */
  badge?: React.ReactNode
  /** Nesting depth under the option above it (a department under its agency). */
  depth?: 0 | 1 | 2
  /** A separator line above this option (after an "All ..." choice). */
  separatorBefore?: boolean
  disabled?: boolean
  /** Not listed (a native `<option hidden>` placeholder); still shown in the trigger while it is the value. */
  hidden?: boolean
}

export interface SelectOptionGroup {
  label: React.ReactNode
  options: SelectOption[]
}

export type SelectOptions = ReadonlyArray<SelectOption | SelectOptionGroup>

/** The reserved item value an empty ("") option is rendered with. Never a real option value. */
export const EMPTY_SELECT_VALUE = "__empty__"

export const isOptionGroup = (entry: SelectOption | SelectOptionGroup): entry is SelectOptionGroup =>
  Array.isArray((entry as SelectOptionGroup).options)

export function flattenOptions(options: SelectOptions): SelectOption[] {
  return options.flatMap((entry) => (isOptionGroup(entry) ? entry.options : [entry]))
}

export interface SimpleSelectProps {
  options: SelectOptions
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  placeholder?: React.ReactNode
  /** A leading icon in the trigger, before the value (a filter icon). */
  triggerIcon?: React.ReactNode
  /** The trigger shows the active look (a filter that is set): brand edge, selected fill. */
  active?: boolean
  size?: OverlaySize
  disabled?: boolean
  required?: boolean
  name?: string
  form?: string
  autoComplete?: string
  id?: string
  /** Native hover hint on the visible trigger. */
  title?: string
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  "aria-label"?: string
  "aria-labelledby"?: string
  "aria-describedby"?: string
  "aria-invalid"?: boolean | "true" | "false"
  /** Required semantics for forms that own their validation without browser constraint validation. */
  "aria-required"?: React.AriaAttributes["aria-required"]
  /** Layout classes for the trigger only (width, margins). Never colour, type or radius (rule 14). */
  className?: string
  "data-testid"?: string
  onBlur?: React.FocusEventHandler<HTMLButtonElement>
  onFocus?: React.FocusEventHandler<HTMLButtonElement>
}

/** The first invalid control of `el`'s form in tree order is `el` (or `el` has no form). */
function isFirstInvalid(el: HTMLSelectElement): boolean {
  const form = el.form
  if (!form) return true
  for (const control of Array.from(form.elements) as Array<HTMLElement & { willValidate?: boolean; validity?: ValidityState }>) {
    if (control.willValidate && control.validity && !control.validity.valid) return control === el
  }
  return true
}

export const SimpleSelect = React.forwardRef<HTMLButtonElement, SimpleSelectProps>(function SimpleSelect(
  {
    options,
    value: valueProp,
    defaultValue,
    onValueChange,
    placeholder,
    triggerIcon,
    active,
    size = "md",
    disabled,
    required,
    name,
    form,
    autoComplete,
    id,
    title,
    open,
    defaultOpen,
    onOpenChange,
    className,
    onBlur,
    onFocus,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
    "aria-required": ariaRequired,
    "data-testid": testId,
  },
  ref,
) {
  const flat = React.useMemo(() => flattenOptions(options), [options])
  if (process.env.NODE_ENV !== "production" && flat.some((o) => o.value === EMPTY_SELECT_VALUE)) {
    throw new Error(`SimpleSelect: "${EMPTY_SELECT_VALUE}" is reserved for the empty option; use "" instead`)
  }
  const [inner, setInner] = React.useState(defaultValue ?? "")
  // An uncontrolled field nobody has touched follows its default (a native select with options that arrive later
  // starts on the first enabled one); once the user chooses, or on a form reset, the field is theirs.
  const touched = React.useRef(false)
  React.useEffect(() => {
    if (valueProp === undefined && !touched.current && defaultValue !== undefined && defaultValue !== inner && !flat.some((o) => o.value === inner)) setInner(defaultValue)
  }, [valueProp, defaultValue, inner, flat])
  const current = valueProp ?? inner
  const [invalid, setInvalid] = React.useState(false)
  const [, forceSync] = React.useReducer((n: number) => n + 1, 0)
  const triggerRef = React.useRef<HTMLButtonElement | null>(null)
  const nativeRef = React.useRef<HTMLSelectElement | null>(null)

  const currentOption = flat.find((o) => o.value === current)
  // A listed "" option is the reserved item; a hidden or missing current value shows as the placeholder.
  const radixValue = currentOption && !currentOption.hidden ? (current === "" ? EMPTY_SELECT_VALUE : current) : ""
  const placeholderNode = currentOption?.hidden ? currentOption.label : placeholder

  const handleValueChange = (next: string) => {
    // Radix gives a RAW "" only from its own hidden form select when that select cannot find the controlled value among
    // its native options yet (a value set by the parent right after mount): an echo, never a choice. A real empty option
    // arrives as EMPTY_SELECT_VALUE. Taking the echo would reset the field to "" right after the parent set it.
    if (next === "") return
    const value = next === EMPTY_SELECT_VALUE ? "" : next
    touched.current = true
    if (valueProp === undefined) setInner(value)
    setInvalid(false)
    onValueChange?.(value)
  }

  // Native form reset: an uncontrolled field returns to its default; a controlled one re-syncs its hidden select.
  const resetTo = defaultValue ?? ""
  React.useEffect(() => {
    const formEl = nativeRef.current?.form
    if (!formEl) return
    const onReset = () => {
      window.setTimeout(() => {
        touched.current = false
        if (valueProp === undefined) setInner(resetTo)
        setInvalid(false)
        forceSync()
      }, 0)
    }
    formEl.addEventListener("reset", onReset)
    return () => formEl.removeEventListener("reset", onReset)
  }, [valueProp, resetTo])

  const bridged = name !== undefined || required !== undefined || form !== undefined
  const renderItem = (o: SelectOption) =>
    o.hidden ? null : (
      <React.Fragment key={o.value === "" ? EMPTY_SELECT_VALUE : o.value}>
        {o.separatorBefore ? <SelectSeparator /> : null}
        <SelectItem
          value={o.value === "" ? EMPTY_SELECT_VALUE : o.value}
          disabled={o.disabled}
          icon={o.icon}
          iconTone={o.iconTone}
          iconTile={o.iconTile}
          swatch={o.swatch}
          description={o.description}
          badge={o.badge}
          depth={o.depth}
          textValue={o.textValue}
        >
          {o.label}
        </SelectItem>
      </React.Fragment>
    )

  return (
    <>
      <Select look="standard" value={radixValue} onValueChange={handleValueChange} disabled={disabled} open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
        <SelectTrigger
          ref={(node) => {
            triggerRef.current = node
            if (typeof ref === "function") ref(node)
            else if (ref) ref.current = node
          }}
          id={id}
          title={title}
          size={size}
          className={cn(active && overlay.triggerActive, className)}
          data-active={active ? "" : undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid ?? (invalid || undefined)}
          aria-required={ariaRequired ?? (required || undefined)}
          data-testid={testId}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {triggerIcon ? (
            <>
              <span data-trigger-icon="" aria-hidden="true" className={overlay.triggerLeading}>{triggerIcon}</span>
              <SelectValue placeholder={placeholderNode} className="min-w-0 flex-1 text-left" />
            </>
          ) : (
            <SelectValue placeholder={placeholderNode} />
          )}
        </SelectTrigger>
        <SelectContent>
          {options.map((entry, i) =>
            isOptionGroup(entry) ? (
              <SelectGroup key={`group-${i}`}>
                <SelectLabel>{entry.label}</SelectLabel>
                {entry.options.map(renderItem)}
              </SelectGroup>
            ) : (
              renderItem(entry)
            ),
          )}
        </SelectContent>
      </Select>
      {bridged ? (
        <select
          ref={nativeRef}
          aria-hidden="true"
          tabIndex={-1}
          className="sr-only"
          data-native-select-bridge=""
          name={name}
          required={required}
          disabled={disabled}
          form={form}
          autoComplete={autoComplete}
          value={current}
          onChange={() => {}}
          onInvalid={(event) => {
            // The hidden select cannot show the browser's message or take focus usefully: mark the trigger invalid
            // and move focus to it when it is the form's first invalid control.
            event.preventDefault()
            setInvalid(true)
            const el = event.currentTarget
            if (isFirstInvalid(el)) window.setTimeout(() => triggerRef.current?.focus(), 0)
          }}
        >
          {/* Nothing chosen (placeholder) or a value not in the list: the native select would fall back to its first
              option and submit that. A leading option holding the current value keeps FormData and `required` exact
              (a leading "" option is the native placeholder, so `required` fails on it). */}
          {currentOption ? null : <option value={current} hidden />}
          {flat.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.textValue ?? (typeof o.label === "string" ? o.label : o.value)}
            </option>
          ))}
        </select>
      ) : null}
    </>
  )
})
